import type { BrowserOptions } from '@sentry/react';

type TracesSampler = NonNullable<BrowserOptions['tracesSampler']>;
type SamplingContext = Parameters<TracesSampler>[0];

/**
 * How much of what the browser does is traced, as a fraction from 0 to 1.
 *
 * The span quota is one pool for every environment, and it was running out
 * part-way through each period — after which Sentry drops everything,
 * including the production Web Vitals the traces exist for. So:
 *
 *  - Production page loads keep the 5% they had. These carry the Web Vitals.
 *  - Production in-app navigations drop to 1%: they carry no vitals and
 *    outnumber page loads several times over.
 *  - Dev and staging are not traced. They were sampled at 20%, four times
 *    production's rate, out of the same pool.
 * @param stage - The deployment stage (`VITE_ENV`).
 * @param pageLoad - Whether the trace is a page load (see `isPageLoad`).
 * @returns The fraction of such traces to keep.
 */
export function traceSampleRate(stage: unknown, pageLoad: boolean): number {
  if (stage !== 'production') return 0;
  return pageLoad ? 0.05 : 0.01;
}

/**
 * Whether the trace Sentry is asking about is a page load.
 *
 * The sampler is not told the trace's op (`pageload`, `navigation`): Sentry
 * adds `sentry.op` to the span only after the sampling decision. What it does
 * pass is `sentry.origin`, which is `auto.pageload.<integration>` for page
 * loads (`auto.pageload.browser` from `browserTracingIntegration`).
 * @param attributes - The attributes Sentry passes to `tracesSampler`.
 * @returns True for a page load.
 */
export function isPageLoad(attributes: SamplingContext['attributes']): boolean {
  const origin = attributes?.['sentry.origin'];
  return typeof origin === 'string' && origin.startsWith('auto.pageload');
}

/**
 * The `tracesSampler` for `Sentry.init`. A trace continued from somewhere that
 * already decided keeps that decision; otherwise see `traceSampleRate`.
 * @param stage - The deployment stage (`VITE_ENV`).
 * @returns The sampler.
 */
export function createTracesSampler(stage: unknown): TracesSampler {
  return ({ attributes, inheritOrSampleWith }) =>
    inheritOrSampleWith(traceSampleRate(stage, isPageLoad(attributes)));
}

/**
 * Our own hosts — `farmshare.co` and its subdomains, over https — which get
 * the `sentry-trace` / `baggage` headers that tie a browser trace to the
 * API's. Anchored at the end of the host, so `farmshare.co.evil.example` and
 * `farmshare.com` don't get them.
 *
 * No `g` flag: a global regex remembers where its last match ended, so tested
 * against one URL after another it matched only some of them, and those
 * requests went out without the headers.
 */
export const FARMSHARE_URL =
  /^https:\/\/([a-z0-9-]+\.)*farmshare\.co(?::\d+)?(\/|\?|#|$)/;

/**
 * Every kind of downloaded file the browser reports (the resource timing
 * `initiatorType`s), so none of them gets a span of its own. A page here loads
 * 50-100 script chunks, plus images, iframes (Stripe) and analytics beacons,
 * and each sampled page load sent one span for every one — Sentry bills per
 * span, so that was most of the quota, spent on data nobody reads. The Web
 * Vitals, and the spans for our own API calls (`fetch` / `xmlhttprequest`,
 * which Sentry traces separately), are still sent.
 */
export const IGNORED_RESOURCE_SPANS = [
  'audio',
  'beacon',
  'body',
  'css',
  'early-hints',
  'embed',
  'frame',
  'icon',
  'iframe',
  'image',
  'img',
  'input',
  'link',
  'navigation',
  'object',
  'other',
  'ping',
  'script',
  'track',
  'video',
].map((initiatorType) => `resource.${initiatorType}`);
